import { watchWithUndo } from '../../common/utilities/WatchWithUndo';
import { EmailValidationHelper } from '../helpers/EmailValidationHelper';
import { CommonErrorCode } from '../helpers/ErrorCodes';
import { Module } from '../Module';
import { ServerService } from '../services/ServerService';
import {
  ISAMLAuthenticationProvider,
  ISiteAuthenticationSettings,
  SiteAuthenticationSettingsService,
} from '../services/SiteAuthenticationSettingsService';
import { ToasterService, ToastType } from '../services/ToasterService';
import { WindowLocationService } from '../../commonInfra/services/WindowLocationService';

interface ISAMLAuthenticationProviderScope extends ng.IScope {
  downloadUrl: (evt: Event, url: string) => void;
  downloadLog: (evt: Event) => void;
  isUsable: () => boolean;
  provider: ISAMLAuthenticationProvider;
  removeSAMLMetadata: () => void;
  samlMetadataFile: File;
  setSAMLAttributeMapping: () => void;
  strings: _.Dictionary<string>;
  test: () => void;
  uploadSAMLMetadata: () => void;
  userAttributes: IUserAttributes;
  isValidUsername: (username: string) => boolean;
}

interface IUserAttributes extends _.Dictionary<string> {}

const downloadLogEndpoint = 'siteSAMLLog';

Module.instance().directive('tbSamlAuthenticationProvider', <any[]>[
  '$q',
  '$translate',
  'FileUploadApiPrefix',
  'ServerService',
  'SiteAuthenticationSettingsService',
  (
    $q: ng.IQService,
    $translate: ng.translate.ITranslateService,
    fileUploadApiPrefix: string,
    server: ServerService,
    siteAuthenticationSettingsService: SiteAuthenticationSettingsService,
  ) => {
    return {
      restrict: 'E',
      templateUrl: 'tbSAMLAuthenticationProvider.html',
      replace: true,
      scope: {
        provider: '=', // establishes two-way binding with parent scope
      },
      link: (scope: ISAMLAuthenticationProviderScope, element: JQuery, attr: ng.IAttributes) => {
        const toaster = ToasterService.instance;
        function handleUpdate(promise: ng.IPromise<any>): ng.IPromise<any> {
          promise.catch((reason: number) => {
            let translateKey = 'unexpectedError';
            if (reason === CommonErrorCode.InsecureSAMLEndpoint) {
              translateKey = 'siteAuthenticationIdPImportFailureInsecureEndpoint';
            } else if (reason === CommonErrorCode.InvalidSAMLIdpMetadata) {
              translateKey = 'siteAuthenticationIdPImportFailureInvalidMetadata';
            }
            toaster.pop({ type: ToastType.Error, text: $translate.instant(translateKey)});
          }).then(() => siteAuthenticationSettingsService.getSiteAuthenticationSettings().then((settings: ISiteAuthenticationSettings) => {
            let provider = siteAuthenticationSettingsService.findProvider(settings, scope.provider.id);
            angular.copy(provider, scope.provider);
          }));
          return promise;
        }
        const translateValues = {
          SERVER_LEVEL_AUTH: server.siteAuthDefaultLabel(),
          SERVER_NAME: server.serverName()
        };
        function setUserAttributes(userAttributes: IUserAttributes) {
          scope.userAttributes = userAttributes;
          scope.$digest();
        }
        scope.downloadUrl = (evt: Event, url: string) => {
          evt.preventDefault();
          WindowLocationService.download(url);
        };
        scope.downloadLog = (evt: Event) => {
          scope.downloadUrl(evt, fileUploadApiPrefix + downloadLogEndpoint);
        };
        scope.isUsable = () => {
          return !!(scope.provider.identityProviderEntityId);
        };
        scope.removeSAMLMetadata = () => {
          handleUpdate(siteAuthenticationSettingsService.removeSAMLMetadata(scope.provider.id));
        };
        scope.setSAMLAttributeMapping = () => {
          handleUpdate(siteAuthenticationSettingsService.setSAMLAttributeMapping(scope.provider.id, scope.provider.attributeMapping))
            .then(() => toaster.pop({ type: ToastType.Info, text: $translate.instant('siteAuthenticationAttributesSaved')}));
        };
        scope.strings = {
          siteAuthenticationType:                 $translate.instant(server.isTableauIdSupported() ? 'siteAuthenticationType': 'siteAuthenticationTypeNonTableauIDDesc'),
          defaultSiteAuthenticationType:          $translate.instant(server.isTableauIdSupported() ? 'siteAuthenticationTypeTableau': 'siteAuthenticationServerLevelDefaultAuth', translateValues),
          siteAuthenticationStep1:                $translate.instant('siteAuthenticationStep1', translateValues),
          siteAuthenticationExportFromSpOption2:  $translate.instant('siteAuthenticationExportFromSpOption2', translateValues),
          siteAuthenticationEntityId:             $translate.instant('siteAuthenticationEntityId', translateValues),
          siteAuthenticationImportToIdP:          $translate.instant('siteAuthenticationImportToIdP', translateValues),
          siteAuthenticationExportMetadataFromIdP:$translate.instant('siteAuthenticationExportMetadataFromIdP', translateValues),
          siteAuthenticationStep4:                $translate.instant('siteAuthenticationStep4', translateValues),
          siteAuthenticationStep5Desc:            $translate.instant('siteAuthenticationStep5Desc', translateValues),
          siteAuthenticationTableauAttribute:     $translate.instant('siteAuthenticationTableauAttribute', translateValues),
          usernameMapping:                        $translate.instant(server.isTableauIdSupported() ? 'email' : 'usernameOrEmail'),
          usernameMappingDescription:             $translate.instant(server.isTableauIdSupported() ? 'siteAuthenticationEmailsMustMatch' : 'siteAuthenticationUsernameMustMatch', translateValues),
          siteAuthenticationNameAssertionsDesc:   $translate.instant('siteAuthenticationNameAssertionsDesc', translateValues),
          siteAuthenticationTroubleshootOption1:  $translate.instant('siteAuthenticationTroubleshootOption1', translateValues),
          siteAuthenticationTroubleshootOption2:  $translate.instant('siteAuthenticationTroubleshootOption2', translateValues),
          siteAuthenticationTroubleshootOption3:  $translate.instant(server.isTableauIdSupported() ? 'siteAuthenticationTroubleshootOption3' : 'siteAuthenticationTroubleshootOption3NonTableauID')
        };
        scope.test = () => {
          WindowLocationService.open(
            scope.provider.testLoginUrl,
            'testLogin',
            { left: 20, top: 20, width: 500, height: 600, toolbar: 1, resizable: 1 });
        };
        scope.uploadSAMLMetadata = () => {
          handleUpdate(siteAuthenticationSettingsService.uploadSAMLMetadata(scope.provider.id, scope.samlMetadataFile))
            .then(() => toaster.pop({ type: ToastType.Info, text: $translate.instant('siteAuthenticationIdPImportSuccess')}));
        };
        function notBlank(s: string): boolean {
          return s && /\S+/.test(s);
        }
        scope.isValidUsername = server.isTableauIdSupported() ? EmailValidationHelper.isValidEmail : notBlank;
        watchWithUndo(scope, 'provider.allowEmbeddedAuthentication', (val: boolean) => {
          return siteAuthenticationSettingsService.setAllowEmbeddedAuthentication(scope.provider.id, val);
        });

        function getHostName(url: string): string {
          // can't use URL since it's not supported in IE (or node)
          const urlParsingNode = document.createElement('a');
          urlParsingNode.setAttribute('href', url);
          return urlParsingNode.hostname;
        }

        function getBaseDomain(url: string): string {
          return getHostName(url).split('.').slice(-2).join('.');
        }

        function isRunningUnderTest(): boolean {
          return window.hasOwnProperty('mocha');
        }

        function setupMessageHandler(baseDomain: string) {
          // Enable cross-origin access to window.opener (this window) from the new window, provided they share the same base domain
          if (!isRunningUnderTest()) {
            document.domain = baseDomain;
          }

          // Test login window is expected to postMessage bearing resolved UserDetails
          const win = angular.element(window);
          const evtName = 'message.tbSamlAuthenticationProvider.testLogin';
          win.on(evtName, (evt: JQuery.TriggeredEvent) => {
            const originalEvt: MessageEvent = <any>evt.originalEvent;
            if (getHostName(originalEvt.origin) !== getHostName(scope.provider.testLoginUrl) && !isRunningUnderTest()) {
              return;
            }
            const userAttributes = <IUserAttributes> JSON.parse(originalEvt.data);
            setUserAttributes(userAttributes);
            if (!isRunningUnderTest()) {
              // (TFS 700140) This line causes the Karma debug window to close, when run via the unit tests
              (<any>originalEvt.source).close();
            }
          });
          element.on('$destroy', () => win.off(evtName));
        }

        // setup 'message' event handling only if it makes sense to do so
        const baseDomain = getBaseDomain(WindowLocationService.getOrigin());
        const testLoginBaseDomain = getBaseDomain(scope.provider.testLoginUrl);
        if (baseDomain === testLoginBaseDomain) {
          setupMessageHandler(baseDomain);
        }
      }
    };
  }
]);
